import {
  mergeContractManagementClauses,
  mergeContractManagementObligations,
  type ContractManagementClauseCreate,
  type ContractManagementObligationCreate,
  ClauseCategory,
  ContractManagementObligationType,
} from '@/api';

/**
 * Extract error message from various error types
 */
function extractErrorMessage(err: unknown, defaultMessage: string): string {
  if (err instanceof Error) {
    return err.message;
  }
  if (typeof err === 'object' && err !== null && 'detail' in err) {
    const detail = err.detail;
    return String(detail);
  }
  return defaultMessage;
}

/**
 * Merge data interface for all entity types
 */
export interface MergeData {
  leftUid: string;
  rightUid: string;
  mergedData: Record<string, unknown>;
}

/**
 * Service for handling merge operations across all Contract Management entities.
 * Provides a centralized, consistent interface for merging entities with proper
 * error handling and data transformation.
 */
export const ContractManagementMergeService = {
  /**
   * Merge two Clause entities
   * @param mergeData - The merge data containing left/right UIDs and merged field values
   * @returns Promise that resolves when merge is complete
   */
  async mergeClauses(mergeData: MergeData): Promise<void> {
    try {
      // Transform merged data to match API contract
      const mergedClauseData: ContractManagementClauseCreate = {
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must normalise to null
        title: (mergeData.mergedData.title as string | null)?.trim() || null,
        text: (mergeData.mergedData.text as string | null)?.trim() ?? '',
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must fall through to the next operand
        category: (mergeData.mergedData.category as ClauseCategory | null) || ClauseCategory.OTHER,
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must normalise to null
        description: (mergeData.mergedData.description as string | null)?.trim() || null,
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must normalise to null
        clause_number: (mergeData.mergedData.clause_number as string | null)?.trim() || null,
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must normalise to null
        section_path: (mergeData.mergedData.section_path as string | null)?.trim() || null,
      };

      await mergeContractManagementClauses({
        body: mergedClauseData,
        query: {
          left_model_uid: mergeData.leftUid,
          right_model_uid: mergeData.rightUid,
        },
      });
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Failed to merge clauses. Please try again.'), {
        cause: error,
      });
    }
  },

  /**
   * Merge two Obligation entities
   * @param mergeData - The merge data containing left/right UIDs and merged field values
   * @returns Promise that resolves when merge is complete
   */
  async mergeObligations(mergeData: MergeData): Promise<void> {
    try {
      // Transform merged data to match API contract
      const mergedObligationData: ContractManagementObligationCreate = {
        name: (mergeData.mergedData.name as string | null)?.trim() ?? '',
        type:
          // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must fall through to the next operand
          (mergeData.mergedData.type as ContractManagementObligationType | null) ||
          ContractManagementObligationType.OTHER,
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must normalise to null
        trigger_event: (mergeData.mergedData.trigger_event as string | null)?.trim() || null,
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must normalise to null
        description: (mergeData.mergedData.description as string | null)?.trim() || null,
      };

      await mergeContractManagementObligations({
        body: mergedObligationData,
        query: {
          left_model_uid: mergeData.leftUid,
          right_model_uid: mergeData.rightUid,
        },
      });
    } catch (error) {
      throw new Error(
        extractErrorMessage(error, 'Failed to merge obligations. Please try again.'),
        { cause: error }
      );
    }
  },
};
